import {
  extract,
  findRequired,
  getMapEntries,
  groupBy,
  requiredAt,
  sumBy,
  uniqueExtract,
  vyDate,
} from '@vy/utils';
import { NormalizedProductUsageDataBreakdown } from './normalized-product-usage-data-breakdown';
import { formatDateByResolution } from '../usage-data-date-formatter';
import { CustomerUsageBreakdown, UsageByDateBreakdown } from '@vy/types';
import { ProductUsageBreakdownDateResolution } from '../../product-usage-breakdown-chart-types/product-usage-breakdown-date-resolution';

export function normalizeUsageDataByDateResolution(
  usageByDateBreakdown: UsageByDateBreakdown[],
  dateResolution: ProductUsageBreakdownDateResolution,
): NormalizedProductUsageDataBreakdown[] {
  const dataByDateResolution = groupBy(usageByDateBreakdown, ({ date }) =>
    formatDateByResolution(date, dateResolution),
  );

  // Order by each bucket's real date — formatted month labels ("Aug, 25") don't sort chronologically as strings.
  return getMapEntries(dataByDateResolution)
    .sort(
      ([, firstGroup], [, secondGroup]) =>
        vyDate(requiredAt(firstGroup, 0).date).valueOf() - vyDate(requiredAt(secondGroup, 0).date).valueOf(),
    )
    .map(([date, usageByDate]) => {
      const summedUnits = sumBy(usageByDate, ({ units }) => units);
      const summedPrice = sumBy(usageByDate, ({ price }) => price ?? 0);
      const customerUsage = extract(usageByDate, 'customerUsage');
      const summedCustomerUsage = sumCustomerUsage(customerUsage);

      return {
        date,
        units: summedUnits,
        price: summedPrice,
        customerUsage: summedCustomerUsage,
      };
    });
}

function sumCustomerUsage(customerUsage: CustomerUsageBreakdown[][]): CustomerUsageBreakdown[] {
  const allUsageData = customerUsage.flat();
  const uniqueCustomerIds = uniqueExtract(allUsageData, 'customerId');

  return uniqueCustomerIds.map<CustomerUsageBreakdown>((customerId) => {
    const customerUsageData = allUsageData.filter((usage) => usage.customerId === customerId);
    const summedUnits = sumBy(customerUsageData, (usage) => usage.units);
    const summedPrice = sumBy(customerUsageData, (usage) => usage.price ?? 0);

    const matchingCustomer = findRequired(customerUsageData, (usage) => usage.customerId === customerId);
    const { customerName } = matchingCustomer;

    return {
      customerId,
      customerName,
      units: summedUnits,
      price: summedPrice,
    };
  });
}
